import { useEffect, useMemo, useState } from "react";
import { listYggCycles, getYggDirectorQueue, bulkApproveYggDirector } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck, Users, Clock, Award } from "lucide-react";

const CODE_LABELS = {
  terfi_edebilir: "Terfi Edebilir", mevcut_rolde_devam: "Mevcut Rolde Devam",
  alaninin_uzmani: "Alanının Uzmanı", anahtar_calisan: "Anahtar Çalışan", tespit_icin_erken: "Tespit İçin Erken",
};

export default function YggDirectorPanel({ directorId }) {
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState(null);
  const [queue, setQueue] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [eligibility, setEligibility] = useState("uygun");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listYggCycles().then((d) => {
      const active = (d.items || []).filter((c) => c.status === "active");
      setCycles(active);
      if (active[0]) setCycleId(active[0].id);
    });
  }, []);

  const reload = () => {
    if (!cycleId || !directorId) return;
    getYggDirectorQueue(cycleId, directorId).then(setQueue);
  };
  useEffect(() => { reload(); setSelected(new Set()); }, [cycleId, directorId]);

  const approvableIds = useMemo(() => {
    const ids = [];
    (queue?.groups || []).forEach((g) => g.items.forEach((i) => { if (i.can_approve) ids.push(i.employee_id); }));
    return ids;
  }, [queue]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleGroup = (g) => {
    const ids = g.items.filter((i) => i.can_approve).map((i) => i.employee_id);
    const allIn = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    ids.forEach((id) => allIn ? next.delete(id) : next.add(id));
    setSelected(next);
  };

  const approve = async () => {
    if (selected.size === 0) { toast.error("Onaylanacak kişi seçin"); return; }
    setBusy(true);
    try {
      const res = await bulkApproveYggDirector(cycleId, {
        director_id: directorId,
        employee_ids: Array.from(selected),
        promotion_eligibility: eligibility,
        director_note: note,
      });
      toast.success(`${res.approved} kişi için direktör onayı verildi`);
      setSelected(new Set()); setNote("");
      reload();
    } catch { toast.error("Onaylanamadı"); } finally { setBusy(false); }
  };

  if (cycles.length === 0) {
    return <div className="card-surface p-8 text-center text-sm text-slate-500" data-testid="ygg-director-empty">Aktif bir YGG döngüsü yok. İK yeni döngü başlattığında ekibinin kayıtları burada görünecek.</div>;
  }

  const counts = queue?.counts || {};

  return (
    <div className="space-y-4" data-testid="ygg-director-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-eyebrow">Direktör Panosu</div>
          <h3 className="font-display text-xl font-semibold">Organizasyonum — YGG Onayları</h3>
          <p className="text-xs text-slate-500 mt-0.5">Yönetici değerlendirmesi tamamlanan kayıtları tek tek veya toplu onaylayabilirsin.</p>
        </div>
        {cycles.length > 1 && (
          <Select value={cycleId || ""} onValueChange={setCycleId}>
            <SelectTrigger className="w-64 bg-white" data-testid="ygg-director-cycle-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ygg-director-kpis">
        <Kpi icon={ShieldCheck} label="Onayımı Bekleyen" value={counts.pending_director ?? "—"} tone="amber" testid="ygg-dir-kpi-pending" />
        <Kpi icon={Clock} label="Önceki Aşamada" value={counts.waiting_prior ?? "—"} tone="slate" testid="ygg-dir-kpi-waiting" />
        <Kpi icon={CheckCircle2} label="Onayladıklarım" value={counts.approved ?? "—"} tone="emerald" testid="ygg-dir-kpi-approved" />
        <Kpi icon={Award} label="Kalibre Edilen" value={counts.calibrated ?? "—"} tone="sky" testid="ygg-dir-kpi-calibrated" />
      </div>

      {/* Bulk action bar */}
      <div className="card-surface p-3 flex items-center gap-2 flex-wrap sticky top-2 z-10 border-amber-200 bg-amber-50/80 backdrop-blur" data-testid="ygg-director-bulkbar">
        <Button size="sm" variant="outline" className="bg-white"
          onClick={() => setSelected(selected.size === approvableIds.length ? new Set() : new Set(approvableIds))}
          data-testid="ygg-director-select-all">
          {selected.size === approvableIds.length && approvableIds.length > 0 ? "Seçimi Temizle" : `Bekleyenlerin Tümünü Seç (${approvableIds.length})`}
        </Button>
        <span className="text-sm font-semibold text-amber-900" data-testid="ygg-director-selected-count">{selected.size} seçili</span>
        <div className="flex-1" />
        <Select value={eligibility} onValueChange={setEligibility}>
          <SelectTrigger className="w-36 h-9 bg-white" data-testid="ygg-director-eligibility"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="uygun">Uygun</SelectItem>
            <SelectItem value="gözlemde">Gözlemde</SelectItem>
            <SelectItem value="beklemede">Beklemede</SelectItem>
          </SelectContent>
        </Select>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ortak direktör notu (ops.)" className="w-56 h-9 bg-white" data-testid="ygg-director-note" />
        <Button size="sm" onClick={approve} disabled={busy || selected.size === 0} className="bg-amber-600 hover:bg-amber-700" data-testid="ygg-director-approve">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />} Seçilenleri Onayla
        </Button>
      </div>

      {/* Groups by manager */}
      {!queue ? (
        <div className="card-surface p-8 text-center text-sm text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Yükleniyor…</div>
      ) : (queue.groups || []).length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-slate-500">Organizasyonunda bu döngüye dahil çalışan bulunamadı.</div>
      ) : (
        (queue.groups || []).map((g) => (
          <div key={g.manager_id} className="card-surface p-4" data-testid={`ygg-director-group-${g.manager_id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" />
                <span className="font-semibold text-sm">{g.manager_name}</span>
                <span className="text-xs text-slate-500">{g.manager_role}</span>
                {g.pending > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{g.pending} onay bekliyor</span>}
              </div>
              {g.pending > 0 && (
                <button className="text-xs text-sky-700 font-semibold hover:underline" onClick={() => toggleGroup(g)} data-testid={`ygg-director-group-select-${g.manager_id}`}>
                  Grubu seç / bırak
                </button>
              )}
            </div>
            <div className="divide-y divide-slate-50">
              {g.items.map((i) => (
                <div key={i.employee_id} className={`flex items-center gap-3 py-2 ${i.can_approve ? "" : "opacity-70"}`} data-testid={`ygg-director-row-${i.employee_id}`}>
                  <input type="checkbox" disabled={!i.can_approve} checked={selected.has(i.employee_id)} onChange={() => toggle(i.employee_id)} className="accent-amber-600 w-4 h-4" data-testid={`ygg-director-check-${i.employee_id}`} />
                  {i.avatar ? <img src={i.avatar} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-slate-200" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{i.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{i.role}{i.department ? ` · ${i.department}` : ""}</div>
                  </div>
                  <div className="hidden md:flex items-center gap-1.5 flex-wrap justify-end">
                    {i.comp_avg != null && <Chip tone="sky">Yetkinlik {i.comp_avg}</Chip>}
                    {i.pys_letter_pre && <Chip tone="indigo">PYS {i.pys_letter_pre}</Chip>}
                    {i.risk_status && <Chip tone={i.risk_status === "yüksek" ? "rose" : i.risk_status === "orta" ? "amber" : "slate"}>Risk: {i.risk_status}</Chip>}
                    {i.mobility && <Chip tone="slate">Mobilite: {i.mobility}</Chip>}
                  </div>
                  <StatusBadge item={i} />
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function StatusBadge({ item }) {
  const s = item.stage_status || {};
  if (s.calibration === "completed") return <span className="text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap bg-sky-100 text-sky-800">{CODE_LABELS[item.talent_code] || "Kalibre Edildi"}</span>;
  if (s.director_review === "completed") return <span className="text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap bg-emerald-100 text-emerald-800">Onaylandı{item.promotion_eligibility ? ` · ${item.promotion_eligibility}` : ""}</span>;
  if (item.can_approve) return <span className="text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap bg-amber-100 text-amber-800">Onayımı Bekliyor</span>;
  if (s.survey !== "completed") return <span className="text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap bg-slate-100 text-slate-500">Anket Bekleniyor</span>;
  return <span className="text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap bg-slate-100 text-slate-500">Yönetici Bekleniyor</span>;
}

function Chip({ tone, children }) {
  const map = {
    sky: "bg-sky-50 text-sky-800 border-sky-100", indigo: "bg-indigo-50 text-indigo-800 border-indigo-100",
    rose: "bg-rose-50 text-rose-800 border-rose-100", amber: "bg-amber-50 text-amber-800 border-amber-100",
    slate: "bg-slate-50 text-slate-600 border-slate-100",
  };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${map[tone]}`}>{children}</span>;
}

function Kpi({ icon: Icon, label, value, tone, testid }) {
  const map = {
    amber: "border-amber-200 bg-amber-50 text-amber-800", slate: "border-slate-200 bg-slate-50 text-slate-600",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800", sky: "border-sky-200 bg-sky-50 text-sky-800",
  };
  return (
    <div className={`rounded-lg border p-3 ${map[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
